import { Injectable } from '@angular/core';
import { formatDate } from '@angular/common';
import { DATE_FORMATS, DATE_TIME_FORMATTER, TIME_MERIDIAN } from '../constants';
import { MONTHS, } from '@qts-sdp-ui/shared-data';
import { DatePipe } from '@angular/common';
import * as i0 from "@angular/core";
import * as i1 from "@angular/common";
export class DateTimeService {
    constructor(datePipe) {
        this.datePipe = datePipe;
        this.convertTimeto24Hr = (time12h) => {
            const [time, modifier] = time12h.split(' ');
            const [hours, minutes] = time.split(':');
            let hrVal = hours;
            if (hours === '12')
                hrVal = '00';
            if (modifier === 'PM')
                hrVal = (parseInt(hrVal, 10) + 12).toString();
            hrVal = hrVal.toString().length === 1 ? `0${hrVal}` : hrVal;
            return `${hrVal}:${minutes}`;
        };
    }
    get defaultDateFormate() {
        return 'm-d-yy';
    }
    get defaultDateTimeFormate() {
        return 'm-d-yy hh:mm:ss';
    }
    formate(dateTime, formate = DATE_FORMATS.M_D_YYYY) {
        if (!dateTime)
            return '';
        const formatter = new Intl.DateTimeFormat('en-US', DATE_TIME_FORMATTER[formate]);
        const validateDateTime = dateTime.replace(' +0000', '');
        const newDateTime = new Date(validateDateTime);
        if (!newDateTime.valueOf())
            return '';
        return formatter
            ?.format(newDateTime)
            ?.replace(/\//g, '-')
            ?.replace(',', '');
    }
    convertToMMDDYYYY(dateString) {
        const [year, month, day] = dateString.split('-');
        return `${month}/${day}/${year}`;
    }
    isSameOrAfter(date) {
        if (!date)
            return false;
        const givenDateTime = new Date(date);
        const now = new Date();
        return givenDateTime >= now;
    }
    isSameOrBefore(date) {
        if (!date)
            return false;
        const givenDateTime = new Date(date);
        const now = new Date();
        return givenDateTime <= now;
    }
    subtractMinutes(date, minutes) {
        const millisecondsPerMinute = 60000; // 60,000 milliseconds in a minute
        const newDate = new Date(date.getTime() - minutes * millisecondsPerMinute);
        return newDate;
    }
    addMinutes(date, minutes) {
        const millisecondsPerMinute = 60000; // 60,000 milliseconds in a minute
        const newDate = new Date(date.getTime() + minutes * millisecondsPerMinute);
        return newDate;
    }
    convertDateToIso(date) {
        if (date)
            return new Date(date)?.toISOString()?.split('.')[0] ?? '';
        return '';
    }
    formatDateRange(startDate, endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);
        const startMonth = start.getMonth() + 1;
        const startDay = start.getDate();
        const startYear = start.getFullYear();
        const endMonth = end.getMonth() + 1;
        const endDay = end.getDate();
        const endYear = end.getFullYear();
        if (start.toDateString() === end.toDateString()) {
            return `${startMonth}-${startDay}-${startYear}`;
        }
        return `${startMonth}-${startDay}-${startYear} to ${endMonth}-${endDay}-${endYear}`;
    }
    formatTimeRange(startTime, endTime) {
        const formattedStartTime = this.convertTo12Hour(startTime);
        const formattedEndTime = this.convertTo12Hour(endTime);
        return `${formattedStartTime} to ${formattedEndTime}`;
    }
    convertDateFormatForRequest(inputDate, dateFormat) {
        if (!inputDate)
            return '';
        const formatedInputDate = inputDate.replace(' +0000', '');
        const date = new Date(formatedInputDate);
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const day = date.getDate();
        const returnMonth = month < 10 ? '0' + month : month;
        const returnDay = day < 10 ? '0' + day : day;
        if (dateFormat === DATE_FORMATS.MM_DD_YYYY) {
            return `${returnMonth}-${returnDay}-${year}`;
        }
        else if (dateFormat === DATE_FORMATS.M_D_YYYY) {
            return `${month}-${day}-${year}`;
        }
        else {
            return `${year}-${returnMonth}-${returnDay}`;
        }
    }
    convertTo12Hour(time) {
        if (!time)
            return '';
        // eslint-disable-next-line prefer-const
        let [hours, minutes] = time.split(':').map((num) => parseInt(num, 10));
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12 || 12;
        return `${hours}:${minutes < 10 ? '0' + minutes : minutes}${ampm}`;
    }
    calculateEndTime(startDate, durationInMinutes) {
        const formatter = new Intl.DateTimeFormat(undefined, DATE_TIME_FORMATTER[DATE_FORMATS.MM_DD_YYYY_HH_MM_AM_PM]);
        const endTime = new Date(startDate.getTime() + durationInMinutes * 60000);
        return {
            sourceDate: startDate,
            formatted: formatter
                .format(endTime)
                .replace(/(am|pm)/i, (match) => match.toUpperCase())
                .replace(/\//g, '-')
                .replace(',', ''),
            resultDate: endTime,
        };
    }
    getDateDifferenceInMinutes(startDate, endDate) {
        const diffInMilliseconds = Math.abs(endDate.getTime() - startDate.getTime());
        const minutes = Math.floor(diffInMilliseconds / (1000 * 60));
        return minutes;
    }
    getTimeInAMPMFormatWithoutSeconds(date) {
        const time = date.toLocaleTimeString('en-US', {
            hour12: true,
            hour: '2-digit',
            minute: '2-digit',
        });
        return this.trimLeadingZero(time);
    }
    getTimeFormatWithoutSeconds(date) {
        const time = date.toLocaleTimeString('en-US', {
            hour12: false,
            hour: '2-digit',
            minute: '2-digit',
        });
        return this.trimLeadingZero(time);
    }
    trimLeadingZero(timeString) {
        return timeString.replace(/^0/, '');
    }
    formatDuration(totalMinutes) {
        if (!totalMinutes)
            return '';
        const validatedTotalMinutes = parseInt(totalMinutes, 10);
        const MINUTES_PER_HOUR = 60;
        const MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR;
        const MINUTES_PER_MONTH = 30 * MINUTES_PER_DAY;
        const MINUTES_PER_YEAR = 365 * MINUTES_PER_DAY; // Leap is ignore
        const timestamp = validatedTotalMinutes * 60 * 1000;
        const date = new Date(timestamp);
        const years = Math.floor(validatedTotalMinutes / MINUTES_PER_YEAR);
        const months = Math.floor(validatedTotalMinutes / MINUTES_PER_MONTH) % 12;
        const days = Math.floor(validatedTotalMinutes / MINUTES_PER_DAY) % 30;
        const hours = date.getUTCHours();
        const minutes = date.getUTCMinutes();
        const durationParts = [];
        if (years)
            durationParts.push(`${years} year${years > 1 ? 's' : ''}`);
        if (months)
            durationParts.push(`${months} month${months > 1 ? 's' : ''}`);
        if (days)
            durationParts.push(`${days} day${days > 1 ? 's' : ''}`);
        if (hours)
            durationParts.push(`${hours} hr`);
        if (minutes)
            durationParts.push(`${minutes} min`);
        return durationParts.join(', ');
    }
    addMinText(totalMinutes) {
        if (!totalMinutes)
            return '';
        return totalMinutes + ' min';
    }
    isValidDate(dateString) {
        const date = new Date(dateString);
        return !isNaN(date.getTime());
    }
    getDaysInMonth(year, month) {
        return new Date(year, month, 0).getDate();
    }
    getWeaklyRange(year, month) {
        const weekRanges = [];
        const startDate = new Date(year, month - 1, 1); // Create a date for the first day of the month
        const endDate = new Date(year, month, 0); // Create a date for the last day of the month
        const currentDate = startDate;
        while (currentDate <= endDate) {
            const startDay = currentDate.getDate();
            currentDate.setDate(currentDate.getDate() + 6); // Move to the end of the week
            let endDay = currentDate.getDate();
            if (currentDate > endDate) {
                endDay = endDate.getDate();
            }
            weekRanges.push(`${MONTHS[currentDate.getMonth()]} ${startDay}-${endDay}`);
            currentDate.setDate(currentDate.getDate() + 1); // Move to the next day
        }
        return weekRanges;
    }
    calculateWeekDays() {
        const weekDays = [];
        const currentDate = new Date();
        const dayOfWeek = currentDate.getDay();
        const diff = currentDate.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1); // Adjust if Sunday
        const monday = new Date(currentDate.setDate(diff));
        for (let i = 0; i < 7; i++) {
            const date = new Date(monday);
            date.setDate(monday.getDate() + i);
            const actualDate = formatDate(date, 'yyyy-MM-dd', 'en-US');
            weekDays.push({
                date: actualDate,
                formattedDate: this.datePipe.transform(actualDate, 'MMM d, EEE') ?? '',
            });
        }
        return weekDays;
    }
    getWeekDaysRange(originalStartDate = '') {
        const currentDate = originalStartDate
            ? new Date(originalStartDate)
            : new Date();
        const startDate = currentDate.getDay();
        const diff = currentDate.getDate() - startDate + (startDate === 0 ? -6 : 1); // Adjust if Sunday
        const monday = new Date(currentDate.setDate(diff));
        const date = new Date(monday);
        date.setDate(monday.getDate() + 6);
        const endDate = date.getDay();
        return [startDate, endDate];
    }
    transform(originalDate, format) {
        return this.datePipe.transform(originalDate, format);
    }
    getMondayDate(selectedDate) {
        const dayOfWeek = selectedDate.getDay(); // Get the day of the week (0 for Sunday, 1 for Monday, ..., 6 for Saturday)
        const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Calculate the offset to Monday
        const mondayDate = new Date(selectedDate.getTime() + mondayOffset * 24 * 60 * 60 * 1000); // Calculate Monday's date
        return mondayDate;
    }
    getSundayDate(selectedDate) {
        const dayOfWeek = selectedDate.getDay(); // Get the day of the week (0 for Sunday, 1 for Monday, ..., 6 for Saturday)
        const sundayOffset = dayOfWeek === 0 ? -6 : -dayOfWeek; // Calculate the offset to Monday
        const mondayDate = new Date(selectedDate.getTime() + sundayOffset * 24 * 60 * 60 * 1000); // Calculate Monday's date
        return mondayDate;
    }
    convertSecToHhMmSsFormat(seconds = 0, ignoreSec = false) {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const remainingSeconds = seconds % 60;
        if (ignoreSec) {
            return `${this.padZero(hours)}:${this.padZero(minutes)}`;
        }
        return `${this.padZero(hours)}:${this.padZero(minutes)}:${this.padZero(remainingSeconds)}`;
    }
    padZero(value) {
        return value < 10 ? `0${value}` : `${value}`;
    }
    static getLocalTimeZone() {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    }
    getMonthDaysRange(currentDate) {
        const weekRanges = [];
        const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
        const startOfWeek = this.getMondayDate(new Date(startOfMonth.getFullYear(), startOfMonth.getMonth(), startOfMonth.getDate()));
        while (startOfWeek <= endOfMonth) {
            const endOfWeek = new Date(startOfWeek);
            endOfWeek.setDate(endOfWeek.getDate() + 6);
            weekRanges.push({
                startDate: new Date(startOfWeek),
                endDate: new Date(endOfWeek),
            });
            startOfWeek.setDate(startOfWeek.getDate() + 7);
        }
        return weekRanges;
    }
    getTimeArray(timeInterval = 60) {
        const totalTimeArray = [];
        for (let i = 0; i < 24; i++) {
            const hourName = this.getHourName(i);
            const hourCode = i < 10 ? '0' + i : i;
            const meridianName = i < 12 ? TIME_MERIDIAN.AM : TIME_MERIDIAN.PM;
            if (timeInterval === 60) {
                totalTimeArray.push({
                    name: `${hourName}:00`,
                    code: `${i}:00`,
                    dName: `${hourName}:00 ${meridianName}`,
                    dCode: `${hourCode}:00`, // For Api
                });
            }
            else {
                for (let j = 0; j < 60 / timeInterval; j++) {
                    const minuteCode = j === 0 ? `00` : timeInterval * j;
                    totalTimeArray.push({
                        name: `${hourName}:${minuteCode}`,
                        code: `${i}:${minuteCode}`,
                        dName: `${hourName}:${minuteCode} ${meridianName}`,
                        dCode: `${hourCode}:${minuteCode}`, // For Api
                    });
                }
            }
        }
        return totalTimeArray;
    }
    getTimepickerControlValue(control) {
        const controlValue = control.value ?? '';
        if (!controlValue)
            return null;
        if (typeof controlValue === 'object')
            return controlValue;
        return {
            name: controlValue,
            code: controlValue,
            dCode: this.convertTimeto24Hr(controlValue),
            dName: controlValue,
        };
    }
    getHourName(hour) {
        if (hour === 0)
            return 12;
        if (hour <= 12)
            return hour;
        return hour - 12;
    }
    /**
     * Helper to convert seconds to hour
     * @param seconds
     * @returns time in hour
     */
    convertSecondToHour(seconds) {
        if (!seconds)
            return '';
        const hr = seconds / 3600;
        const min = seconds / 60;
        return hr < 1 ? `${min}min` : `${hr}h`;
    }
    /**
     * Convert date string to utc
     * @param dateStr
     * @returns
     */
    toUTC(dateStr) {
        // Create a Date object from the date string, assuming local time at midnight
        const localDate = new Date(dateStr);
        // Extract the year, month, and date components
        const year = localDate.getUTCFullYear();
        const month = localDate.getUTCMonth(); // Note: Month is zero-based
        const date = localDate.getUTCDate();
        // Create a new Date object in UTC time at midnight
        return new Date(year, month, date);
    }
    static { this.ɵfac = function DateTimeService_Factory(t) { return new (t || DateTimeService)(i0.ɵɵinject(i1.DatePipe)); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: DateTimeService, factory: DateTimeService.ɵfac, providedIn: 'root' }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DateTimeService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], () => [{ type: i1.DatePipe }], null); })();
